/* ============================================================
   VIVA VOCE — vv-pdp-main.js

   Purpose:
     Drives the custom PDP web component <vv-pdp-main>: variant
     selection (color swatches + size grid), price/availability
     sync, quantity stepper (incl. per-variant inventory cap as of
     VV-228), accordions, sticky mobile CTA, size-guide & shop-pay
     modals.

   Public API:
     Custom element `vv-pdp-main` (auto-defined on load, guarded
     by customElements.get). No exported functions - all behavior
     is wired via connectedCallback against data-vv-* attributes
     and children rendered by sections/vv-pdp-main.liquid.

   Upstream dependencies:
     - sections/vv-pdp-main.liquid: renders the <script
       data-vv-variants> JSON payload this file parses, and the
       data-* attributes read in connectedCallback (labels,
       backorder-values, urgency-threshold, money-format, and as
       of VV-228 data-quantity-error-max, sourced from the
       products.product.quantity_error_max locale key). As of
       VV-226 it also renders data-gallery-slide-template (the
       localized "Image {{ index }} of {{ count }}" string, used
       for the aria-live gallery status), with __INDEX__/__COUNT__
       sentinels for client-side substitution. Every gallery media
       element (main-stage image, rail thumb, mobile-carousel
       slide, dot) carries data-vv-media-id; ALL media renders
       unconditionally - there is no variant-attachment attribute
       to read any more (removed in the gallery-refinements
       follow-up to VV-226). The desktop rail also renders
       data-vv-thumbs-up / data-vv-thumbs-down scroll buttons
       inside [data-vv-thumb-rail-wrap]. As of the rail-height-sync fix,
       the section CSS's .vv-pdp__thumb-rail-wrap consumes a
       --vv-pdp-rail-h custom property that #bindGalleryHeightSync sets
       on this element (height: var(--vv-pdp-rail-h, auto)) - correct
       desktop rail bounding now depends on this file running, not on
       CSS alone.
     - As of VV-228, each variant object in data-vv-variants may
       carry inventory_management (string | null) and
       inventory_policy ("deny" | "continue") alongside
       inventory_quantity (number | null - null means untracked).
       #isCappable(variant) is the single predicate gating both
       the quantity-stepper max (#applyQtyCap) and the "Only N
       left" urgency text: a variant is cappable only when
       available, inventory_management is present, policy is
       'deny', and inventory_quantity is a number. It fails open
       (no cap, no urgency count) for untracked or
       continue-selling variants, so the stepper max and the
       displayed urgency count can never drift from each other.
     - Horizon's <product-form-component> (parent form) consumes
       the hidden variant/quantity inputs this file updates.

   Downstream consumers:
     - templates/product.json and templates/product.drop.json
       (both render sections/vv-pdp-main.liquid, which loads this
       file as a module script).

   Failure modes:
     - #readVariants() returns [] on malformed JSON; every
       lookup against an empty variants array degrades to
       "no variant found" rather than throwing.
     - Quantity cap fails open: any variant that isn't
       confirmed cappable (see #isCappable above) leaves
       maxQty at Infinity - no cap, no disabled +, no urgency
       count. This is deliberate: a missing/ambiguous inventory
       field must never block a purchasable item.
     - Gallery refinements (follow-up to VV-226): #updateGallery no
       longer filters media by variant - it only PRIORITIZES the
       variant's featured image (makes it active, scrolls the rail
       and mobile carousel to it) via #showMedia. Every media item
       stays in the DOM and visible at all times; clicking a
       thumb/dot/arrow only changes which image is displayed - it
       never changes selectedOptions or the chosen variant. Since
       nothing is ever hidden, thumb/dot aria-labels are the static
       SSR labels (rendered against the full, unfiltered
       product.media set) and are never recomputed client-side.
     - Gallery refinements Minor-fix pass: #showMedia promotes the target
       main-stage <img> to loading="eager"/fetchPriority="high" (and fires
       a best-effort .decode()) before toggling .is-active, so a
       prioritized-but-not-yet-loaded image starts fetching immediately
       instead of waiting on its SSR loading="lazy". #bindThumbRailScroll's
       up/down buttons use aria-disabled (not the native disabled
       attribute) at scroll bounds, and their click handlers no-op when
       aria-disabled="true", so reaching a bound never blurs a keyboard
       user's focus to <body> the way native `disabled` would.
     - Rail-height-sync fix (VV-226 follow-up): the desktop gallery grid
       gives [data-vv-thumb-rail-wrap] no definite height of its own - an
       `auto` grid row sizes to the tallest item's max-content, and the
       rail's max-content is its full stacked thumbnail list regardless
       of overflow-y:auto. #bindGalleryHeightSync mirrors the main
       stage's clientHeight onto a --vv-pdp-rail-h custom property
       (consumed by .vv-pdp__thumb-rail-wrap's
       `height: var(--vv-pdp-rail-h, auto)` in the section CSS) via a
       ResizeObserver, so the wrap gets a genuine bounded height the rail
       can then overflow-scroll against. Disabled below the 1080px
       breakpoint (property cleared) so the mobile carousel is never
       affected; the `auto` CSS fallback only covers a JS-disabled page
       gracefully - it does not by itself bound the rail when a product
       has more thumbnails than the main image is tall.
     - Chevron-init fix (rail-height-sync follow-up): #bindThumbRailScroll
       runs before #bindGalleryHeightSync in connectedCallback, so its
       initial updateButtons() call fires before --vv-pdp-rail-h is set -
       the rail is still unbounded (no overflow) at that point, so both
       chevrons landed aria-disabled="true" on load even when the rail
       genuinely overflows. #bindThumbRailScroll now exposes its
       updateButtons as this.updateThumbButtons, and
       #bindGalleryHeightSync's syncGalleryHeight() calls
       this.updateThumbButtons?.() every time it sets or clears the
       property (initial bind, ResizeObserver fire, matchMedia
       breakpoint change) so button state is always recomputed against
       the current bound height.
     - Pure client-side DOM sync, no network calls, idempotent -
       #updateState can be called any number of times per
       interaction with no side effects beyond DOM/URL sync.

   Performance: standard; all work is synchronous DOM
     read/write against a small, page-local variant array (no
     network I/O, no timers other than the transitions library
     provides natively via CSS). The mobile-carousel scroll
     listener is debounced (120ms) to avoid re-computing the
     active slide on every scroll frame.
   ============================================================ */
class VvPdpMain extends HTMLElement {
  connectedCallback() {
    this.variants = this.#readVariants();
    this.backorderValues = (this.dataset.backorderValues || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

    this.priceEl = this.querySelector('[data-vv-price]');
    this.compareEl = this.querySelector('[data-vv-compare]');
    this.urgencyEl = this.querySelector('[data-vv-urgency]');
    this.variantInput = this.querySelector('input[name="id"]');
    this.qtyInput = this.querySelector('input[name="quantity"]');
    this.qtyValEl = this.querySelector('[data-vv-qty]');
    this.atcBtn = this.querySelector('[data-vv-atc]');
    this.shoppayBtn = this.querySelector('[data-vv-shoppay]');
    this.qtyRow = this.querySelector('[data-vv-qty-row]');
    this.emailWrap = this.querySelector('[data-vv-backorder-wrap]');
    this.emailInput = this.querySelector('[data-vv-backorder-email]');
    this.orderNotice = this.querySelector('[data-vv-order-notice]');
    this.madeToOrderBox = this.querySelector('[data-vv-made-to-order]');
    this.optionValueEls = this.querySelectorAll('[data-vv-option-value]');

    this.labels = {
      add: this.dataset.addLabel || 'Add to Bag',
      soldOut: this.dataset.soldOutLabel || 'Sold Out',
      unavailable: this.dataset.unavailableLabel || 'Unavailable',
      notify: this.dataset.notifyLabel || 'Notify Me',
      urgency: this.dataset.urgencyTemplate || 'Hurry! Only [qty] left in size [size]',
      urgencyThreshold: parseInt(this.dataset.urgencyThreshold || '10', 10),
    };
    this.labels.quantityErrorMax = this.dataset.quantityErrorMax || '';

    // selectedOptions[position-1] = value
    const current = this.variants.find((v) => String(v.id) === String(this.variantInput && this.variantInput.value));
    const initial = current || this.variants.find((v) => v.available) || this.variants[0];
    this.selectedOptions = initial ? initial.options.slice() : [];

    this.#bindSwatches();
    this.#bindSizes();
    this.#bindQty();
    this.#bindAccordions();
    this.#bindViewMore();
    this.#bindModals();
    this.#bindStickyBar();
    this.#bindGallery();

    this.#updateState();
  }

  #readVariants() {
    const node = this.querySelector('[data-vv-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return [];
    }
  }

  #bindSwatches() {
    this.swatches = Array.from(this.querySelectorAll('[data-vv-swatch]'));
    this.swatches.forEach((btn) => {
      btn.addEventListener('click', () => {
        const pos = parseInt(btn.dataset.position, 10);
        this.selectedOptions[pos - 1] = btn.dataset.value;
        this.#updateState();
      });
    });
  }

  #bindSizes() {
    this.sizeBtns = Array.from(this.querySelectorAll('[data-vv-size]'));
    this.sizeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-oos')) return;
        const pos = parseInt(btn.dataset.position, 10);
        this.selectedOptions[pos - 1] = btn.dataset.value;
        this.#updateState();
      });
    });
  }

  #bindQty() {
    this.qty = parseInt((this.qtyInput && this.qtyInput.value) || '1', 10) || 1;
    this.maxQty = Infinity; // recomputed per-variant by #applyQtyCap
    this.decBtn = this.querySelector('[data-vv-qty-dec]');
    this.incBtn = this.querySelector('[data-vv-qty-inc]');
    if (this.decBtn) this.decBtn.addEventListener('click', () => this.#setQty(this.qty - 1));
    if (this.incBtn) this.incBtn.addEventListener('click', () => this.#setQty(this.qty + 1));
  }

  #setQty(n) {
    const upper = this.maxQty === Infinity ? n : Math.min(n, this.maxQty);
    this.qty = Math.max(1, upper);
    if (this.qtyValEl) this.qtyValEl.textContent = this.qty;
    if (this.qtyInput) this.qtyInput.value = this.qty;
    this.#updateQtyIncState();
  }

  // Single source of truth: does this variant have a real, finite, deny-policy tracked stock number?
  // Fails open (returns false) for: no variant, unavailable, untracked (no inventory_management),
  // continue-selling (policy !== 'deny'), or non-numeric inventory_quantity.
  #isCappable(variant) {
    return (
      !!variant &&
      variant.available === true &&
      !!variant.inventory_management &&
      variant.inventory_policy === 'deny' &&
      typeof variant.inventory_quantity === 'number'
    );
  }

  #applyQtyCap(variant) {
    const cappable = this.#isCappable(variant);
    this.maxQty = cappable ? Math.max(0, variant.inventory_quantity) : Infinity;
    this.#setQty(this.qty); // re-clamp current selection to the new cap
  }

  #updateQtyIncState() {
    if (!this.incBtn) return;
    const atCap = this.maxQty !== Infinity && this.qty >= this.maxQty;
    this.incBtn.disabled = atCap;
    this.incBtn.setAttribute('aria-disabled', atCap ? 'true' : 'false');
    this.incBtn.setAttribute(
      'aria-label',
      atCap && this.labels.quantityErrorMax
        ? this.labels.quantityErrorMax.replace('{{ maximum }}', String(this.maxQty))
        : 'Increase quantity'
    );
  }

  #bindAccordions() {
    this.querySelectorAll('[data-vv-accordion-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const acc = btn.closest('[data-vv-accordion]');
        const open = acc.classList.toggle('is-open');
        const icon = btn.querySelector('[data-vv-accordion-icon]');
        if (icon) icon.textContent = open ? '−' : '+';
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  #bindViewMore() {
    const btn = this.querySelector('[data-vv-view-more]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const body = btn.closest('[data-vv-accordion-body]') || this;
      const content = body.querySelector('[data-vv-desc-content]');
      const gradient = body.querySelector('[data-vv-desc-gradient]');
      if (content) content.classList.add('is-expanded');
      if (gradient) gradient.style.display = 'none';
      btn.style.display = 'none';
    });
  }

  #bindModals() {
    // Size guide
    this.querySelectorAll('[data-vv-open-sizeguide]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.#openModal('sizeguide');
      });
    });
    // Shop Pay sample plans
    this.querySelectorAll('[data-vv-open-shoppay]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.#openModal('shoppay');
      });
    });
    this.querySelectorAll('[data-vv-modal-close], [data-vv-modal-mask]').forEach((el) => {
      el.addEventListener('click', () => this.#closeModals());
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.#closeModals();
    });

    // Size guide cm/in + mobile tabs
    this.#initSizeGuide();
  }

  #openModal(kind) {
    const modal = this.querySelector(`[data-vv-modal="${kind}"]`);
    const mask = this.querySelector(`[data-vv-modal-mask="${kind}"]`);
    if (modal) modal.classList.add('is-open');
    if (mask) mask.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const sticky = document.querySelector('[data-vv-sticky-bar]');
    if (sticky) sticky.style.display = 'none';
    if (kind === 'sizeguide') this.#applySizeGuideLayout();
  }

  #closeModals() {
    this.querySelectorAll('[data-vv-modal], [data-vv-modal-mask]').forEach((el) => el.classList.remove('is-open'));
    document.body.style.overflow = '';
    const sticky = document.querySelector('[data-vv-sticky-bar]');
    if (sticky) sticky.style.display = '';
  }

  #initSizeGuide() {
    const root = this.querySelector('[data-vv-modal="sizeguide"]');
    if (!root) return;

    this.#buildSizeGuideContent(root);

    root.querySelectorAll('[data-vv-sg-tab]').forEach((btn) => {
      btn.addEventListener('click', () => this.#switchSgTab(btn.dataset.vvSgTab));
    });
    window.addEventListener('resize', () => {
      if (root.classList.contains('is-open')) this.#applySizeGuideLayout();
    });
  }

  /* Pull the size-chart page content out of the <template>, route the first image into the
     image column and the table(s)/notes into the chart column. Measurements are rendered exactly
     as the merchant entered them on the page — no unit conversion. */
  #buildSizeGuideContent(root) {
    const source = root.querySelector('[data-vv-sg-source]');
    const imageCol = root.querySelector('[data-vv-sg-image]');
    const tablesCol = root.querySelector('[data-vv-sg-tables]');
    if (!source || !tablesCol) return;

    const holder = document.createElement('div');
    if (source.content) {
      holder.appendChild(source.content.cloneNode(true));
    } else {
      holder.innerHTML = source.innerHTML;
    }

    const img = holder.querySelector('img');
    if (img && imageCol) imageCol.appendChild(img);
    while (holder.firstChild) tablesCol.appendChild(holder.firstChild);
  }

  #switchSgTab(tab) {
    this.sgTab = tab;
    const root = this.querySelector('[data-vv-modal="sizeguide"]');
    const chart = root.querySelector('[data-vv-sg-chart]');
    const image = root.querySelector('[data-vv-sg-image]');
    if (chart) chart.style.display = tab === 'chart' ? 'block' : 'none';
    if (image) image.style.display = tab === 'measure' ? 'flex' : 'none';
    root.querySelectorAll('[data-vv-sg-tab]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.vvSgTab === tab);
    });
  }

  #applySizeGuideLayout() {
    const root = this.querySelector('[data-vv-modal="sizeguide"]');
    const tabs = root.querySelector('[data-vv-sg-tabs]');
    const inner = root.querySelector('[data-vv-sg-inner]');
    const image = root.querySelector('[data-vv-sg-image]');
    const measureTab = root.querySelector('[data-vv-sg-tab="measure"]');
    // VV-222: the "How to Measure" tab only makes sense when the size-chart page
    // supplied a how-to-measure image. When it didn't, the tab renders a blank
    // panel — so hide the tab, and collapse the whole tabs bar since "Size Chart"
    // would be the sole remaining tab. buildSizeGuideContent has already routed
    // any image into the image column by the time this runs.
    const hasMeasureImage = image && image.children.length > 0;
    if (measureTab) measureTab.style.display = hasMeasureImage ? '' : 'none';
    // Single-column, full-width chart on every breakpoint so wide multi-column
    // size charts (SIZE / NUMERIC / BRA / BUST / WAIST / HIPS) fit without any
    // horizontal scrolling. The how-to-measure image lives behind its tab.
    if (tabs) tabs.style.display = hasMeasureImage ? 'block' : 'none';
    if (inner) inner.style.gridTemplateColumns = '1fr';
    // Force the chart tab whenever the measure tab is unavailable, so a stale
    // 'measure' selection can't leave the modal showing an empty image panel.
    if (!this.sgTab || !hasMeasureImage) this.#switchSgTab('chart');
  }

  #bindStickyBar() {
    const bar = document.querySelector('[data-vv-sticky-bar]');
    if (!bar) return;
    const trigger = this.querySelector('[data-vv-cta-row]');

    // VV-211 QA fix: gate states (upcoming/ended) render no purchase area, so
    // [data-vv-cta-row] doesn't exist and there's nothing to "scroll past".
    // Show the gate sticky "Notify Me" CTA immediately instead of observing.
    if (!trigger) {
      if (bar.querySelector('.vv-pdp-sticky__cta--gate')) bar.classList.add('is-visible');
      return;
    }

    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only show when the CTA row has been scrolled past (above viewport),
          // not when it's simply below the fold on page load.
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          bar.classList.toggle('is-visible', scrolledPast);
        });
      },
      { threshold: 0 }
    );
    observer.observe(trigger);
  }

  /* ---------- gallery (VV-226 + gallery refinements) ---------- */
  /* Binds every interactive gallery control once: rail thumbs, dots, the
     desktop prev/next arrows, the rail's up/down scroll buttons,
     arrow-key navigation while focus is inside the stage or rail, and a
     debounced scroll listener that keeps the dots in sync with a manual
     swipe on the mobile carousel. Clicking a thumb/dot/arrow only changes
     the DISPLAYED image - it never touches selectedOptions or the chosen
     variant. */
  #bindGallery() {
    this.galleryStatusEl = this.querySelector('[data-vv-gallery-status]');
    this.gallerySlideTemplate = this.dataset.gallerySlideTemplate || 'Image __INDEX__ of __COUNT__';
    this.activeMediaId = null;

    this.querySelectorAll('[data-vv-thumb]').forEach((btn) => {
      btn.addEventListener('click', () => this.#showMedia(btn.dataset.vvMediaId));
    });
    this.querySelectorAll('[data-vv-dot]').forEach((btn) => {
      btn.addEventListener('click', () => this.#showMedia(btn.dataset.vvMediaId));
    });

    const prevBtn = this.querySelector('[data-vv-gallery-prev]');
    const nextBtn = this.querySelector('[data-vv-gallery-next]');
    if (prevBtn) prevBtn.addEventListener('click', () => this.#stepMedia(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => this.#stepMedia(1));

    const stage = this.querySelector('[data-vv-main-stage]');
    const rail = this.querySelector('[data-vv-thumb-rail]');
    [stage, rail].forEach((scope) => {
      if (!scope) return;
      scope.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        this.#stepMedia(e.key === 'ArrowLeft' ? -1 : 1);
      });
    });

    const scroller = this.querySelector('[data-vv-mobile-scroll]');
    if (scroller) {
      let scrollTimer;
      scroller.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this.#syncActiveFromScroll(scroller), 120);
      });
    }

    this.#bindThumbRailScroll();
    this.#bindGalleryHeightSync();
  }

  /* Desktop-only (>=1080px): bounds [data-vv-thumb-rail-wrap] to the
     rendered height of [data-vv-main-stage] via a --vv-pdp-rail-h custom
     property, so the rail (flex: 1 1 auto; min-height: 0; overflow-y:
     auto in the section CSS) gets a genuine max height to overflow
     against instead of growing the shared CSS-grid row to its own
     max-content. No-ops for single-image products (--no-rail: no rail
     wrap in the DOM). A ResizeObserver on the main stage keeps the value
     current across image-load reflow and viewport resize; the
     matchMedia listener clears the property below 1080px so the mobile
     carousel layout is never touched, and re-applies it if the viewport
     grows back past the breakpoint. */
  #bindGalleryHeightSync() {
    const mainStage = this.querySelector('[data-vv-main-stage]');
    const railWrap = this.querySelector('[data-vv-thumb-rail-wrap]');
    if (!mainStage || !railWrap) return;

    this.desktopMql = window.matchMedia('(min-width: 1080px)');
    this.syncGalleryHeight = () => {
      if (!this.desktopMql.matches) {
        this.style.removeProperty('--vv-pdp-rail-h');
        this.updateThumbButtons?.();
        return;
      }
      this.style.setProperty('--vv-pdp-rail-h', `${mainStage.clientHeight}px`);
      // #bindThumbRailScroll runs first in connectedCallback and computes
      // its initial up/down state before this property is set, so the
      // rail's clientHeight/scrollHeight comparison is taken against the
      // still-unbounded rail (no overflow yet) - both chevrons land
      // disabled. Recompute now that the real bound is in place.
      this.updateThumbButtons?.();
    };

    if ('ResizeObserver' in window) {
      this.railHeightObserver = new ResizeObserver(this.syncGalleryHeight);
      this.railHeightObserver.observe(mainStage);
    }
    this.desktopMql.addEventListener('change', this.syncGalleryHeight);
    this.syncGalleryHeight();
  }

  /* Tears down the rail-height ResizeObserver + matchMedia listener so a
     section re-render in the theme editor (which disconnects/reconnects
     this custom element) doesn't leak an observer per edit. */
  disconnectedCallback() {
    if (this.railHeightObserver) this.railHeightObserver.disconnect();
    if (this.desktopMql && this.syncGalleryHeight) {
      this.desktopMql.removeEventListener('change', this.syncGalleryHeight);
    }
  }

  /* Desktop-only rail scroll controls: up/down buttons step the rail by
     one thumbnail (+ its gap) via native scrollBy, and are dimmed at the
     top/bottom scroll bounds so they never look clickable when there's
     nothing further to scroll. A no-op (buttons render but stay
     permanently dimmed) when the rail's content doesn't overflow the
     main-stage-synced height it's bounded to - e.g. a product with only
     2-3 images.

     Uses aria-disabled (not the native disabled attribute) for the bound
     state: a native disabled button blurs focus to <body> the moment it's
     disabled, which drops a keyboard user out of the rail entirely the
     instant they reach the top/bottom. aria-disabled keeps the button
     focusable - the dim comes from CSS ([aria-disabled="true"], below) and
     the click handlers below no-op instead of relying on the browser to
     block the click. */
  #bindThumbRailScroll() {
    const rail = this.querySelector('[data-vv-thumb-rail]');
    const upBtn = this.querySelector('[data-vv-thumbs-up]');
    const downBtn = this.querySelector('[data-vv-thumbs-down]');
    if (!rail || !upBtn || !downBtn) return;

    const stepSize = () => {
      const thumb = rail.querySelector('[data-vv-thumb]');
      if (!thumb) return rail.clientHeight;
      const gap = parseFloat(getComputedStyle(rail).rowGap || getComputedStyle(rail).gap) || 0;
      return thumb.offsetHeight + gap;
    };

    const updateButtons = () => {
      const atTop = rail.scrollTop <= 0;
      const atBottom = rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 1;
      upBtn.setAttribute('aria-disabled', atTop ? 'true' : 'false');
      downBtn.setAttribute('aria-disabled', atBottom ? 'true' : 'false');
    };

    upBtn.addEventListener('click', () => {
      if (upBtn.getAttribute('aria-disabled') === 'true') return;
      rail.scrollBy({ top: -stepSize(), behavior: 'smooth' });
    });
    downBtn.addEventListener('click', () => {
      if (downBtn.getAttribute('aria-disabled') === 'true') return;
      rail.scrollBy({ top: stepSize(), behavior: 'smooth' });
    });

    let scrollTimer;
    rail.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateButtons, 60);
    });
    window.addEventListener('resize', updateButtons);
    // Exposed so #bindGalleryHeightSync's syncGalleryHeight() can recompute
    // button state after --vv-pdp-rail-h changes - see that method's
    // comment for why the initial call here happens before the rail has
    // its real bounded height.
    this.updateThumbButtons = updateButtons;
    updateButtons();
  }

  /* Ordered (DOM/product.media order) list of every media id in the main
     stage. All product media renders unconditionally now (nothing is
     hidden by variant), so this is simply the full set - the canonical
     order used for prev/next stepping and the aria-live "Image X of Y"
     status. */
  #mediaIds() {
    return Array.from(this.querySelectorAll('[data-vv-main-stage] [data-vv-media-id]')).map((el) =>
      String(el.dataset.vvMediaId)
    );
  }

  #stepMedia(delta) {
    const ids = this.#mediaIds();
    if (!ids.length) return;
    const current = ids.indexOf(this.activeMediaId);
    const from = current === -1 ? 0 : current;
    const next = ids[(from + delta + ids.length) % ids.length];
    this.#showMedia(next);
  }

  /* Toggles .is-active across the stage/rail/dots for mediaId and, unless
     scroll:false (used by the scroll-sync listener to avoid feedback
     loops), scrolls the rail and mobile carousel to match.

     Before flipping .is-active, promotes the target main-stage <img> to
     loading="eager" + fetchPriority="high" so the browser starts fetching
     it immediately - without this, a main-stage image that hasn't yet
     scrolled into view still carries its SSR loading="lazy" and the
     browser can sit on a ~1-2s grey flash before it fetches, which
     undercuts the "prioritize the variant image" feature. Rail thumbnails
     and the mobile-carousel images are never touched here - they stay
     lazy per their SSR markup. */
  #showMedia(mediaId, { scroll = true } = {}) {
    if (mediaId === undefined || mediaId === null) return;
    const id = String(mediaId);
    this.activeMediaId = id;

    const stageImg = this.querySelector(`[data-vv-main-stage] [data-vv-media-id="${id}"] img`);
    if (stageImg) {
      stageImg.loading = 'eager';
      stageImg.fetchPriority = 'high';
      if (typeof stageImg.decode === 'function') stageImg.decode().catch(() => {});
    }

    this.querySelectorAll('[data-vv-media-id]').forEach((el) => {
      el.classList.toggle('is-active', String(el.dataset.vvMediaId) === id);
    });

    if (scroll) {
      const thumb = this.querySelector(`[data-vv-thumb][data-vv-media-id="${id}"]`);
      if (thumb) {
        const rail = thumb.closest('[data-vv-thumb-rail]');
        if (rail) rail.scrollTo({ top: thumb.offsetTop - rail.offsetTop, behavior: 'smooth' });
      }
      const scroller = this.querySelector('[data-vv-mobile-scroll]');
      const slide = scroller && scroller.querySelector(`[data-vv-media-id="${id}"]`);
      if (slide && scroller) {
        scroller.scrollTo({ left: slide.offsetLeft - scroller.offsetLeft, behavior: 'smooth' });
      }
    }

    if (this.galleryStatusEl) {
      const ids = this.#mediaIds();
      const index = ids.indexOf(id) + 1;
      if (index > 0) {
        this.galleryStatusEl.textContent = this.gallerySlideTemplate
          .replace('__INDEX__', index)
          .replace('__COUNT__', ids.length);
      }
    }
  }

  /* Debounced scroll handler (see #bindGallery): finds the mobile-carousel
     slide nearest the scroller's center and syncs the active classes to it
     WITHOUT re-triggering a programmatic scroll (scroll:false) - a manual
     swipe must not fight the browser's own momentum scroll. */
  #syncActiveFromScroll(scroller) {
    const slides = Array.from(scroller.querySelectorAll('[data-vv-media-id]'));
    if (!slides.length) return;
    const center = scroller.scrollLeft + scroller.offsetWidth / 2;
    let closest = slides[0];
    let closestDist = Infinity;
    slides.forEach((slide) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const dist = Math.abs(slideCenter - center);
      if (dist < closestDist) {
        closestDist = dist;
        closest = slide;
      }
    });
    this.#showMedia(closest.dataset.vvMediaId, { scroll: false });
  }

  /* ---------- core variant state ---------- */
  #findVariant() {
    return this.variants.find((v) =>
      v.options.every((opt, i) => {
        if (this.selectedOptions[i] === undefined) return true;
        return opt === this.selectedOptions[i];
      })
    );
  }

  #availableFor(position, value) {
    // a value (in `position`) is available if any variant matches it together
    // with the OTHER currently-selected options and is available
    return this.variants.some((v) => {
      if (v.options[position - 1] !== value) return false;
      const matchesOthers = v.options.every((opt, i) => {
        if (i === position - 1) return true;
        if (this.selectedOptions[i] === undefined) return true;
        return opt === this.selectedOptions[i];
      });
      return matchesOthers && v.available;
    });
  }

  // True if ANY variant carrying this option value is in stock (ignores the
  // other currently-selected options). Used to flag colours that are entirely
  // sold out so the swatch can show that state up-front, before it's clicked.
  #valueHasStock(position, value) {
    return this.variants.some(
      (v) => v.options[position - 1] === value && v.available
    );
  }

  #updateState() {
    const variant = this.#findVariant();

    // Swatch active + sold-out states
    this.swatches.forEach((btn) => {
      const pos = parseInt(btn.dataset.position, 10);
      const soldOut = !this.#valueHasStock(pos, btn.dataset.value);
      btn.classList.toggle('is-active', this.selectedOptions[pos - 1] === btn.dataset.value);
      btn.classList.toggle('is-oos', soldOut);
      btn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
      const base = btn.getAttribute('aria-label') || '';
      const cleaned = base.replace(/\s*\(.*\)$/, '');
      btn.setAttribute('aria-label', soldOut ? `${cleaned} (${this.labels.soldOut})` : cleaned);
    });

    // Size button states (active + OOS for current color)
    this.sizeBtns.forEach((btn) => {
      const pos = parseInt(btn.dataset.position, 10);
      const val = btn.dataset.value;
      const available = this.#availableFor(pos, val);
      const backorder = this.backorderValues.includes(val);
      btn.classList.toggle('is-active', this.selectedOptions[pos - 1] === val);
      btn.classList.toggle('is-oos', !available && !backorder);
      btn.classList.toggle('is-backorder', backorder);
    });

    // Labels — each option's "Name: VALUE" reflects its OWN selected value,
    // keyed by option position (so Jewelry Type, Size, etc. each update).
    const sizeVal = this.#valueForRole('size');
    this.optionValueEls.forEach((el) => {
      const pos = parseInt(el.dataset.position, 10);
      const val = this.selectedOptions[pos - 1];
      if (val) el.textContent = val;
    });

    // Price + variant id
    if (variant) {
      if (this.variantInput) this.variantInput.value = variant.id;
      this.#updateURL(variant);
      this.#updateGallery(variant);
      if (this.priceEl) this.priceEl.innerHTML = this.#money(variant.price);
      if (this.compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.compareEl.innerHTML = this.#money(variant.compare_at_price);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }
    }

    this.#updateCta(variant, sizeVal);
    this.#syncStickyBar();
  }

  /* Append/refresh the ?variant= param so deep-links and refreshes preserve
     the selection — mirrors Shopify-native variant URL behaviour. */
  #updateURL(variant) {
    if (!variant || !window.history || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    if (String(url.searchParams.get('variant')) === String(variant.id)) return;
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ ...window.history.state }, '', url.toString());
  }

  /* Gallery refinements (follow-up to VV-226): PRIORITIZES the selected
     variant's featured image rather than filtering the gallery - makes it
     the active main image and scrolls the rail/mobile carousel to it
     (see #showMedia), falling back to the first media item when the
     variant has no featured media assigned. Every other media item stays
     in the DOM and visible; nothing is hidden. */
  #updateGallery(variant) {
    const ids = this.#mediaIds();
    if (!ids.length) return;
    const featured = variant && variant.featured_media_id ? String(variant.featured_media_id) : null;
    const target = featured && ids.includes(featured) ? featured : ids[0];
    this.#showMedia(target);
  }

  #valueForRole(role) {
    const pos = role === 'color' ? this.colorPosition() : this.sizePosition();
    if (!pos) return null;
    return this.selectedOptions[pos - 1] || null;
  }

  colorPosition() {
    return this.dataset.colorPosition ? parseInt(this.dataset.colorPosition, 10) : null;
  }

  sizePosition() {
    return this.dataset.sizePosition ? parseInt(this.dataset.sizePosition, 10) : null;
  }

  #updateCta(variant, sizeVal) {
    const isBackorder = sizeVal && this.backorderValues.includes(sizeVal);
    const available = variant && variant.available;

    if (isBackorder) {
      if (this.atcBtn) {
        this.atcBtn.textContent = this.labels.notify;
        this.atcBtn.dataset.mode = 'notify';
        this.atcBtn.disabled = false;
        this.atcBtn.type = 'button';
      }
      if (this.shoppayBtn) this.shoppayBtn.style.display = 'none';
      if (this.qtyRow) this.qtyRow.style.display = 'none';
      if (this.emailWrap) this.emailWrap.style.display = '';
      if (this.orderNotice) this.orderNotice.style.display = '';
      if (this.madeToOrderBox) this.madeToOrderBox.style.display = 'none';
      if (this.urgencyEl) {
        this.urgencyEl.textContent = this.dataset.backorderUrgency || 'This size is currently on back order.';
        this.urgencyEl.classList.add('is-muted');
      }
      this.maxQty = Infinity;
      return;
    }

    if (this.atcBtn) {
      this.atcBtn.dataset.mode = 'add';
      this.atcBtn.type = 'submit';
      this.atcBtn.disabled = !available;
      this.atcBtn.textContent = available ? this.labels.add : this.labels.soldOut;
    }
    if (this.shoppayBtn) this.shoppayBtn.style.display = available ? '' : 'none';
    if (this.qtyRow) this.qtyRow.style.display = '';
    if (this.emailWrap) this.emailWrap.style.display = 'none';
    if (this.orderNotice) this.orderNotice.style.display = 'none';
    if (this.madeToOrderBox) this.madeToOrderBox.style.display = '';
    this.#applyQtyCap(variant);
    if (this.urgencyEl) {
      this.urgencyEl.classList.remove('is-muted');
      // VV-228: urgency count MUST use the same #isCappable predicate as the stepper cap,
      // else a tracked continue-selling variant (real inventory_quantity, policy 'continue')
      // would show "Only N left" while the stepper stays uncapped — the exact bug this fixes.
      const cappable = this.#isCappable(variant);
      const qty = cappable ? variant.inventory_quantity : null;
      if (qty !== null && qty > 0 && qty <= this.labels.urgencyThreshold) {
        this.urgencyEl.textContent = this.labels.urgency
          .replace('[qty]', qty)
          .replace('[size]', sizeVal || '');
        this.urgencyEl.style.display = '';
      } else {
        this.urgencyEl.style.display = 'none';
      }
    }
  }

  #syncStickyBar() {
    const bar = document.querySelector('[data-vv-sticky-bar]');
    if (!bar) return;
    const variant = this.#findVariant();
    const priceEl = bar.querySelector('[data-vv-sticky-price]');
    if (priceEl && variant) priceEl.innerHTML = this.#money(variant.price);
  }

  #money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const amountNoDecimals = Math.round(cents / 100).toLocaleString();
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount);
  }
}

if (!customElements.get('vv-pdp-main')) {
  customElements.define('vv-pdp-main', VvPdpMain);
}
